// ── ToggleActiveModal ─────────────────────────────────────────────────────────
// Confirm dialog shown before deactivating (or re-activating) a Kostenart row.
// Deactivate path: fetches usage-count first, shows the warning in the modal.
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Button } from '../../../../shared/ui'
import { useKostenartUsage } from '../api/kostenartenApi'
import type { Kostenart } from '../api/kostenartenApi'

interface Props {
  row: Kostenart | null
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isMutating: boolean
}

export function ToggleActiveModal({ row, open, onClose, onConfirm, isMutating }: Props) {
  const { t } = useTranslation('kostenarten')

  // usage-count is only relevant when deactivating
  const isDeactivating = !!row?.active
  const { data: usageData, isFetching, refetch } = useKostenartUsage(row?.id ?? 0)

  // Fetch usage-count when the modal opens for a deactivation
  useEffect(() => {
    if (open && isDeactivating && row?.id) {
      refetch()
    }
  }, [open, isDeactivating, row?.id, refetch])

  if (!row) return null

  const usageCount = usageData?.count ?? 0

  const modalTitle = isDeactivating
    ? t('deactivateModal.title')
    : t('activateModal.title')

  const bodyText = isDeactivating ? (
    <div className="space-y-3">
      <p className="text-sm text-on-surface-variant">
        {isFetching
          ? '…'
          : usageCount > 0
            ? t('deactivateModal.usageWarning', { count: usageCount })
            : t('deactivateModal.usageNone')
        }
      </p>
      <p className="text-xs text-on-surface-variant">{t('deactivateModal.hint')}</p>
    </div>
  ) : (
    <p className="text-sm text-on-surface-variant">{t('activateModal.hint')}</p>
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      subtitle={<span className="font-mono text-xs">{row.code}</span>}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isMutating}>
            {isDeactivating ? t('deactivateModal.cancel') : t('activateModal.cancel')}
          </Button>
          <Button
            variant={isDeactivating ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={isMutating}
            disabled={isFetching}
          >
            {isDeactivating ? t('deactivateModal.confirm') : t('activateModal.confirm')}
          </Button>
        </>
      }
    >
      {bodyText}
    </Modal>
  )
}
